/**
 * Activity Tracking Utils
 * Utilities for tracking user activities for both authenticated and anonymous users.
 * Handles externalClientId generation and campaign data retrieval.
 */

import { getCookie, setCookie } from './cookies';
import clickoutService from '../services/clickoutService';
import userProfileService from '../services/userProfileService';

/**
 * Get or generate externalClientId for anonymous users
 * @returns {string} External client ID
 */
export const getExternalClientId = () => {
  // Get from Redux state (managed by App.js)
  const { store } = require('../redux/store');
  return store.getState().user.anonymousClientId;
};

/**
 * Get current campaign data from cookies
 * @returns {Object|null} Campaign data { name, expiry } or null if not available
 */
export const getCurrentCampaign = () => {
  const campaignName = getCookie('attributor');
  const expiryStr = getCookie('attributor_expiry');

  if (!campaignName || !expiryStr) {
    return null;
  }

  // Parse expiry date
  let expiry;
  try {
    expiry = new Date(expiryStr);
    if (isNaN(expiry.getTime())) {
      return null;
    }
  } catch (e) {
    return null;
  }

  return {
    name: campaignName,
    expiry: expiry.toISOString()
  };
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has token
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

/**
 * Update attributor cookie if backend returned updated campaign data
 * @param {Object} lastCampaign - Campaign data { name, expiry }
 */
export const updateAttributorFromBackend = (lastCampaign) => {
  if (lastCampaign?.name && lastCampaign.expiry) {
    const currentAttributor = getCookie("attributor");
    const currentExpiryStr = getCookie("attributor_expiry");
    
    // If no current campaign, set the new one
    if (!currentAttributor || !currentExpiryStr || isNaN(new Date(currentExpiryStr).getTime())) {
      console.log(`setting initial attributor ${lastCampaign.name} from backend`);
      setCookie("attributor", lastCampaign.name, new Date(lastCampaign.expiry).toUTCString());
      setCookie("attributor_expiry", new Date(lastCampaign.expiry).toUTCString(), new Date(lastCampaign.expiry).toUTCString());
      return;
    }
    
    // Check if we should update: only if new expiry is later (for same or different campaign)
    let shouldUpdate = false;
    let isExpiryOnlyUpdate = false;
    
    try {
      const currentExpiry = new Date(currentExpiryStr);
      const newExpiry = new Date(lastCampaign.expiry);
      
      // Check if dates are valid
      if (isNaN(newExpiry.getTime())) {
        console.log('Invalid date in lastCampaign.expiry, not updating attributor');
      } else if (newExpiry > currentExpiry) {
        // Update only if backend expiry is later
        shouldUpdate = true;
        isExpiryOnlyUpdate = lastCampaign.name === currentAttributor;
      }
    } catch (e) {
      // If date parsing fails, don't update
      console.log('Error parsing campaign expiry dates, not updating attributor', e);
    }
    
    if (shouldUpdate) {
      const action = isExpiryOnlyUpdate ? 'updating expiry' : `updating attributor from ${currentAttributor} to ${lastCampaign.name}`;
      console.log(`${action} (new expiry: ${new Date(lastCampaign.expiry).toISOString()})`);
      setCookie("attributor", lastCampaign.name, new Date(lastCampaign.expiry).toUTCString());
      setCookie("attributor_expiry", new Date(lastCampaign.expiry).toUTCString(), new Date(lastCampaign.expiry).toUTCString());
    }
  }
};

/**
 * Set attributor from URL parameters (utm_campaign)
 * @param {string} campaign - Campaign name from URL
 * @param {string} [expiry] - Optional expiry date, defaults to 400 days from now
 */
export const setAttributorFromUrl = (campaign, expiry) => {
  if (!campaign) return;
  
  const expiryDate = expiry || new Date(new Date().setDate(new Date().getDate() + 400)).toUTCString();
  
  setCookie('attributor', campaign, expiryDate);
  setCookie('attributor_expiry', expiryDate, expiryDate);
};

/**
 * Get current attributor value
 * @returns {string} Attributor value or empty string
 */
export const getAttributor = () => {
  return getCookie('attributor') || '';
};

/**
 * Get current attributor data for backend API calls
 * @returns {Object} { attributor, attributorExpiry }
 */
export const getAttributorData = () => {
  const attributor = getCookie('attributor');
  const attributorExpiry = getCookie('attributor_expiry');
  return { attributor, attributorExpiry };
};

/**
 * Send search activity
 * @param {Object} searchData - Search data
 * @param {Object} [lastCampaign] - Campaign data
 * @returns {Promise<Array|null>} Updated searches array for authenticated users, true for anonymous, false on error
 */
export const trackSearch = async (searchData, lastCampaign = null) => {

  if (isAuthenticated()) {
    // Authenticated user
    const { store } = require('../redux/store');
    const user = store.getState().user.user;
    const userId = user?.id;
    if (userId) {
      const campaign = lastCampaign || getCurrentCampaign();
      const response = await userProfileService.saveRecentSearch(userId, searchData, campaign);
      
      // Update cookies if backend returned updated campaign data
      updateAttributorFromBackend(response?.lastCampaign);
      
      return response?.recentSearches; // Return the updated searches array
    }
  } else {
    // Anonymous user - get client ID from Redux state (managed by App.js)
    const { store } = require('../redux/store');
    const externalClientId = store.getState().user.anonymousClientId;
    
    if (externalClientId) {
      const campaign = lastCampaign || getCurrentCampaign();
      await userProfileService.saveAnonymousRecentSearch(externalClientId, searchData, campaign);
      return true; // Return true for anonymous (no searches array returned)
    }
  }
  return false;
};

/**
 * Send clickout activity
 * @param {string} searchKey - Search key
 * @param {Object} clickoutData - Clickout data
 * @param {Object} [lastCampaign] - Campaign data
 * @param {Object} [searchData] - Full search data (to create missing search if needed)
 */
export const trackClickout = async (searchKey, clickoutData, lastCampaign = null, searchData = null) => {

  if (isAuthenticated()) {
    // Authenticated user
    const { store } = require('../redux/store');
    const user = store.getState().user.user;
    const userId = user?.id;
    if (userId) {
      const campaign = lastCampaign || getCurrentCampaign();
      const response = await clickoutService.recordClickout(userId, searchKey, clickoutData, campaign, searchData);
      
      // Update cookies if backend returned updated campaign data
      updateAttributorFromBackend(response?.lastCampaign);
      
      return response?.success || false;
    }
  } else {
    // Anonymous user - get client ID from Redux state (managed by App.js)
    const { store } = require('../redux/store');
    const externalClientId = store.getState().user.anonymousClientId;
    
    if (externalClientId) {
      const campaign = lastCampaign || getCurrentCampaign();
      await clickoutService.recordAnonymousClickout(externalClientId, searchKey, clickoutData, campaign, searchData);
      return true;
    }
  }
  return false;
};